"use client";

import { useState } from "react";
import {
  Brain,
  Compass,
  Search,
  FileText,
  X,
  Lightbulb,
  ChevronDown,
  ChevronUp,
  MapPin,
  Route,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import type { AiProfile } from "@/types/ai";
import type { AtlasSearchProcess, AtlasNavigationResult } from "@/lib/atlas/integration";
import { getTextColorForBackground, getSecondaryTextColorForBackground } from "@/lib/utils/colorContrast";
import CognitiveMapGraph from "@/components/atlas/CognitiveMapGraph";
import ConfidenceIndicator from "@/components/atlas/ConfidenceIndicator";

export interface AtlasFullscreenViewProps {
  profile: AiProfile;
  atlasProcess: AtlasSearchProcess;
  atlasNavigationResult?: AtlasNavigationResult | null;
  atlasHistory?: Array<{
    process: AtlasSearchProcess;
    result: AtlasNavigationResult;
    messageId: string;
    timestamp: number;
  }>;
  primaryColor: string;
  secondaryColor: string;
  onClose: () => void;
}

/**
 * Atlas Fullscreen View - State 2: Fullscreen overlay (slide up)
 * Shows complete Atlas navigation information with all details
 */
export default function AtlasFullscreenView({
  profile,
  atlasProcess,
  atlasNavigationResult,
  atlasHistory,
  primaryColor,
  secondaryColor,
  onClose,
}: AtlasFullscreenViewProps) {
  const cardBg = "#ffffff";
  const cardTextColor = getTextColorForBackground(cardBg);
  const cardTextMuted = getSecondaryTextColorForBackground(cardBg);

  const getStatusInfo = () => {
    switch (atlasProcess.status) {
      case "planning":
        return {
          icon: Brain,
          text: "Planning cognitive navigation...",
          desc: "Understanding where this information lives in the cognitive map",
        };
      case "navigating":
        return {
          icon: Compass,
          text: "Navigating spatially...",
          desc: "Moving through the cognitive map to find the right area",
        };
      case "orienting":
        return {
          icon: Search,
          text: "Orienting in map...",
          desc: 'Confirming location - "This information lives here"',
        };
      case "complete":
      case "ready":
        return {
          icon: FileText,
          text: "Navigation complete",
          desc: "Found the right area in the cognitive map",
        };
      default:
        return { icon: FileText, text: "Processing...", desc: "" };
    }
  };

  const statusInfo = getStatusInfo();
  const StatusIcon = statusInfo.icon;
  const [thoughtSummaryExpanded, setThoughtSummaryExpanded] = useState(false);

  const thoughtSummaryLines: string[] = [];
  if (atlasProcess?.message?.trim()) thoughtSummaryLines.push(atlasProcess.message.trim());
  if (atlasNavigationResult?.orientation?.message?.trim())
    thoughtSummaryLines.push(atlasNavigationResult.orientation.message.trim());
  if (atlasNavigationResult?.route?.description?.trim())
    thoughtSummaryLines.push(atlasNavigationResult.route.description.trim());
  const loc =
    atlasNavigationResult?.location?.topicName || atlasNavigationResult?.orientation?.currentTopic;
  const landmarkCount = atlasNavigationResult?.location?.landmarks?.length ?? 0;
  const routeCount = atlasNavigationResult?.orientation?.nearbyTopics?.length ?? 0;
  if (thoughtSummaryLines.length === 0 && atlasNavigationResult) {
    if (loc) thoughtSummaryLines.push(`Located in ${loc}.`);
    if (landmarkCount > 0)
      thoughtSummaryLines.push(
        `${landmarkCount} landmark${landmarkCount !== 1 ? "s" : ""} in this area.`
      );
    if (routeCount > 0)
      thoughtSummaryLines.push(
        `${routeCount} route${routeCount !== 1 ? "s" : ""} available.`
      );
  }
  if (thoughtSummaryLines.length === 0)
    thoughtSummaryLines.push(statusInfo.desc || "Navigation complete.");
  const thoughtSummaryFull = thoughtSummaryLines.join("\n");
  const thoughtSummaryPreview = thoughtSummaryLines[0] ?? thoughtSummaryFull;
  const hasMore = thoughtSummaryLines.length > 1 || thoughtSummaryFull.length > 100;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: "100%" }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: "100%" }}
        transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
        className="fixed inset-0 z-[60] flex flex-col"
        style={{
          background: "rgba(248, 249, 252, 0.72)",
          backdropFilter: "blur(20px) saturate(140%)",
          WebkitBackdropFilter: "blur(20px) saturate(140%)",
        }}
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.96, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.3 }}
          className="flex-1 overflow-y-auto p-4 sm:p-6"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="max-w-4xl mx-auto space-y-6">
            {/* Header */}
            <div
              className="flex items-center justify-between mb-6 p-4 rounded-2xl"
              style={{
                background: "#ffffff",
                border: `1px solid ${primaryColor}25`,
                boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
              }}
            >
              <div className="flex items-center gap-3">
                <div
                  className="p-3 rounded-2xl"
                  style={{
                    background: "#f0eeff",
                    border: `1px solid ${primaryColor}35`,
                  }}
                >
                  <StatusIcon className="w-6 h-6" style={{ color: cardTextColor }} />
                </div>
                <div>
                  <h2 className="text-xl font-bold" style={{ color: cardTextColor }}>
                    Cognitive Navigation
                  </h2>
                  <p className="text-sm opacity-70" style={{ color: cardTextMuted }}>
                    {statusInfo.text}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-full hover:bg-black/5 transition-colors"
                style={{ color: cardTextColor }}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Thought summary */}
            <div
              className="rounded-2xl overflow-hidden"
              style={{
                background: "#ffffff",
                border: `1px solid ${primaryColor}30`,
                boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
              }}
            >
              <button
                type="button"
                onClick={() => setThoughtSummaryExpanded((e) => !e)}
                className="w-full flex items-start gap-3 p-4 text-left hover:bg-black/[0.02] transition-colors"
              >
                <Lightbulb className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: cardTextColor }} />
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-semibold uppercase tracking-wide mb-2" style={{ color: cardTextColor }}>
                    Thought summary
                  </h4>
                  <p className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: cardTextMuted }}>
                    {thoughtSummaryExpanded
                      ? thoughtSummaryFull
                      : thoughtSummaryPreview.length > 120
                        ? thoughtSummaryPreview.slice(0, 120) + "\u2026"
                        : thoughtSummaryPreview}
                  </p>
                </div>
                <span className="flex-shrink-0 mt-1" style={{ color: cardTextColor }}>
                  {thoughtSummaryExpanded ? (
                    <ChevronUp className="w-5 h-5" />
                  ) : (
                    <ChevronDown className="w-5 h-5" />
                  )}
                </span>
              </button>
              {hasMore && (
                <div className="px-4 pb-3 pt-0 border-t border-black/5" style={{ background: "#fafaff" }}>
                  <button
                    type="button"
                    onClick={() => setThoughtSummaryExpanded((e) => !e)}
                    className="text-xs font-medium hover:underline"
                    style={{ color: cardTextColor }}
                  >
                    {thoughtSummaryExpanded ? "Show less" : "Show more"}
                  </button>
                </div>
              )}
            </div>

            {/* Navigation Complete Section */}
            <div
              className="p-4 rounded-2xl"
              style={{
                background: "#ffffff",
                border: `1px solid ${primaryColor}25`,
                boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
              }}
            >
              <div className="flex items-start gap-3">
                <StatusIcon className="w-5 h-5 flex-shrink-0" style={{ color: cardTextColor }} />
                <div className="flex-1">
                  <div className="font-medium mb-1" style={{ color: cardTextColor }}>
                    Navigation complete
                  </div>
                  <div className="text-sm opacity-70" style={{ color: cardTextMuted }}>
                    {statusInfo.desc}
                  </div>
                </div>
              </div>
            </div>

            {/* CURRENT NAVIGATION Section */}
            <div>
              <h3 className="text-sm font-bold uppercase tracking-wide mb-4 opacity-80" style={{ color: cardTextColor }}>
                CURRENT NAVIGATION
              </h3>

              <div
                className="p-5 rounded-2xl mb-4"
                style={{
                  background: "#ffffff",
                  border: `1px solid ${primaryColor}30`,
                  boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                }}
              >
                <div className="flex items-start gap-3 mb-3">
                  <Brain className="w-5 h-5 flex-shrink-0" style={{ color: cardTextColor }} />
                  <div className="flex-1">
                    <h4 className="font-semibold mb-2" style={{ color: cardTextColor }}>
                      Cognitive Navigation Process
                    </h4>
                    <p className="text-sm opacity-80 mb-3">
                      {loc
                        ? `Navigated to ${loc}${landmarkCount > 0 ? ` · ${landmarkCount} landmark${landmarkCount !== 1 ? "s" : ""}` : ""}${routeCount > 0 ? ` · ${routeCount} route${routeCount !== 1 ? "s" : ""} available` : ""}. Ready to answer.`
                        : "Processing cognitive navigation…"}
                    </p>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full" style={{ background: primaryColor }} />
                      <span className="text-xs opacity-70">Navigation Complete</span>
                    </div>
                  </div>
                </div>
              </div>

              <div
                className="p-4 rounded-xl mb-4"
                style={{
                  background: "#ffffff",
                  border: `1px solid ${primaryColor}25`,
                  boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                }}
              >
                <p className="text-sm leading-relaxed opacity-90" style={{ color: cardTextMuted }}>
                  <strong className="opacity-100" style={{ color: cardTextColor }}>
                    I don't search.
                  </strong>{" "}
                  I navigate a pre-built cognitive map: I know where information lives and orient in that space. Like a mental map, not a database query.
                </p>
              </div>

              {atlasNavigationResult?.location && (
                <div
                  className="p-5 rounded-2xl mb-4"
                  style={{
                    background: "#ffffff",
                    border: `1px solid ${primaryColor}30`,
                    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                  }}
                >
                  <div className="flex items-start gap-3 mb-3">
                    <MapPin className="w-5 h-5 flex-shrink-0" style={{ color: cardTextColor }} />
                    <div className="flex-1">
                      <h4 className="text-sm font-bold uppercase tracking-wide mb-2 opacity-90" style={{ color: cardTextColor }}>
                        {loc ? `Current location: ${loc}` : "Current location in cognitive map"}
                      </h4>
                      <div className="text-xl font-bold mb-2" style={{ color: cardTextColor }}>
                        {atlasNavigationResult.location.topicName || "General knowledge area"}
                      </div>
                      {atlasNavigationResult.location.topicId && (
                        <div className="text-xs opacity-60 mb-2 font-mono" style={{ color: cardTextMuted }}>
                          Topic ID: {atlasNavigationResult.location.topicId}
                        </div>
                      )}
                      {atlasNavigationResult.orientation?.message ? (
                        <div className="text-sm italic opacity-80 mb-2" style={{ color: cardTextMuted }}>
                          "{atlasNavigationResult.orientation.message}"
                        </div>
                      ) : (
                        <div className="text-sm italic opacity-80 mb-2" style={{ color: cardTextMuted }}>
                          "This information lives here"
                        </div>
                      )}
                      {atlasNavigationResult.location.landmarks && (
                        <div className="text-xs opacity-70 mt-2" style={{ color: cardTextMuted }}>
                          Found {atlasNavigationResult.location.landmarks.length} landmark
                          {atlasNavigationResult.location.landmarks.length !== 1 ? "s" : ""}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {atlasNavigationResult?.orientation?.nearbyTopics &&
                atlasNavigationResult.orientation.nearbyTopics.length > 0 && (
                  <div
                    className="p-5 rounded-2xl mb-4"
                    style={{
                      background: "#ffffff",
                      border: `1px solid ${primaryColor}25`,
                      boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                    }}
                  >
                    <div className="flex items-start gap-3 mb-4">
                      <Route className="w-5 h-5 flex-shrink-0" style={{ color: cardTextColor }} />
                      <div className="flex-1">
                        <h4 className="text-sm font-bold uppercase tracking-wide mb-3 opacity-90" style={{ color: cardTextColor }}>
                          {routeCount > 0 ? `Available routes (${routeCount})` : "Available routes"}
                        </h4>
                        <div className="space-y-4">
                          {atlasNavigationResult.orientation.nearbyTopics.map((topic, idx) => (
                            <div
                              key={idx}
                              className="p-3 rounded-lg"
                              style={{
                                background: "#f8f7ff",
                                border: `1px solid ${primaryColor}20`,
                              }}
                            >
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex-1">
                                  <div className="font-semibold text-sm mb-1" style={{ color: cardTextColor }}>
                                    {topic.name}
                                  </div>
                                  {topic.id && (
                                    <div className="text-xs opacity-60 font-mono mb-1" style={{ color: cardTextColor }}>
                                      ID: {topic.id}
                                    </div>
                                  )}
                                  {topic.description && (
                                    <div className="text-xs opacity-75 mb-2" style={{ color: cardTextColor }}>
                                      {topic.description}
                                    </div>
                                  )}
                                </div>
                                {topic.familiarity !== undefined && (
                                  <div className="text-xs opacity-70 ml-2" style={{ color: cardTextColor }}>
                                    {(topic.familiarity * 100).toFixed(1)}%
                                  </div>
                                )}
                              </div>
                              {topic.nearbyTopics && topic.nearbyTopics.length > 0 && (
                                <div className="text-xs opacity-70 mb-2" style={{ color: cardTextColor }}>
                                  Connected to {topic.nearbyTopics.length} topic
                                  {topic.nearbyTopics.length !== 1 ? "s" : ""}
                                </div>
                              )}
                              {topic.commonUserIntents && topic.commonUserIntents.length > 0 && (
                                <div className="mt-2">
                                  <div className="text-xs opacity-70 mb-1" style={{ color: cardTextColor }}>
                                    Common intents:
                                  </div>
                                  <div className="flex flex-wrap gap-1">
                                    {topic.commonUserIntents.map((intent, i) => (
                                      <span
                                        key={i}
                                        className="text-xs px-2 py-0.5 rounded"
                                        style={{ background: "#f0eeff", color: cardTextColor }}
                                      >
                                        {intent}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {topic.preferredLandmarks && topic.preferredLandmarks.length > 0 && (
                                <div className="mt-2">
                                  <div className="text-xs opacity-70 mb-1" style={{ color: cardTextColor }}>
                                    Preferred landmarks:
                                  </div>
                                  <div className="flex flex-wrap gap-1">
                                    {topic.preferredLandmarks.map((landmark, i) => (
                                      <span
                                        key={i}
                                        className="text-xs px-2 py-0.5 rounded"
                                        style={{ background: "#ebe4ff", color: secondaryColor }}
                                      >
                                        {landmark}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

              {atlasNavigationResult?.route?.steps && atlasNavigationResult.route.steps.length > 0 && (
                <div
                  className="p-5 rounded-2xl mb-4"
                  style={{
                    background: "#ffffff",
                    border: `1px solid ${primaryColor}25`,
                    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                  }}
                >
                  <h4 className="text-sm font-bold uppercase tracking-wide mb-3 opacity-90" style={{ color: cardTextColor }}>
                    Navigation path ({atlasNavigationResult.route.steps.length} step
                    {atlasNavigationResult.route.steps.length !== 1 ? "s" : ""})
                  </h4>
                  {atlasNavigationResult.route.description && (
                    <p className="text-sm opacity-80 mb-4" style={{ color: cardTextColor }}>
                      {atlasNavigationResult.route.description}
                    </p>
                  )}
                  <div className="space-y-2">
                    {atlasNavigationResult.route.steps.map((step, idx) => (
                      <div
                        key={idx}
                        className="p-3 rounded-lg flex items-center gap-3"
                        style={{
                          background: "#f8f7ff",
                          border: `1px solid ${primaryColor}20`,
                        }}
                      >
                        <div
                          className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
                          style={{ background: primaryColor, color: "#fff" }}
                        >
                          {idx + 1}
                        </div>
                        <div className="flex-1">
                          <div className="text-sm font-medium mb-1" style={{ color: cardTextColor }}>
                            {step.from}
                          </div>
                          <div className="text-xs opacity-60 mb-1" style={{ color: cardTextColor }}>
                            → {step.to}
                          </div>
                          {step.type && (
                            <div className="text-xs opacity-50 italic" style={{ color: cardTextColor }}>
                              Type: {step.type}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {atlasNavigationResult?.sources && atlasNavigationResult.sources.length > 0 && (
                <div
                  className="p-5 rounded-2xl mb-4"
                  style={{
                    background: "#ffffff",
                    border: `1px solid ${primaryColor}25`,
                    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                  }}
                >
                  <h4 className="text-sm font-bold uppercase tracking-wide mb-3 opacity-90" style={{ color: cardTextColor }}>
                    Sources ({atlasNavigationResult.sources.length})
                  </h4>
                  <div className="space-y-2">
                    {atlasNavigationResult.sources.map((source, idx) => (
                      <div
                        key={idx}
                        className="p-3 rounded-lg"
                        style={{
                          background: "#f8f7ff",
                          border: `1px solid ${primaryColor}20`,
                        }}
                      >
                        <div className="flex items-start justify-between mb-1">
                          <div className="flex-1">
                            <div className="font-medium text-sm" style={{ color: cardTextColor }}>
                              {source.title}
                            </div>
                            {source.url && (
                              <div className="text-xs opacity-60 font-mono mt-1 truncate" style={{ color: cardTextColor }}>
                                {source.url}
                              </div>
                            )}
                          </div>
                          <div className="ml-3 flex items-center gap-2">
                            {source.type && (
                              <span
                                className="text-xs px-2 py-0.5 rounded"
                                style={{ background: "#ebe4ff", color: secondaryColor }}
                              >
                                {source.type}
                              </span>
                            )}
                            {source.relevance !== undefined && (
                              <span className="text-sm font-semibold" style={{ color: cardTextColor }}>
                                {(source.relevance * 100).toFixed(2)}%
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {atlasNavigationResult?.location?.landmarks &&
                atlasNavigationResult.location.landmarks.length > 0 && (
                  <div
                    className="p-5 rounded-2xl mb-4"
                    style={{
                      background: "#ffffff",
                      border: `1px solid ${primaryColor}25`,
                      boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                    }}
                  >
                    <h4 className="text-sm font-bold uppercase tracking-wide mb-3 opacity-90" style={{ color: cardTextColor }}>
                      {landmarkCount > 0 ? `Landmarks (${landmarkCount})` : "Landmarks"}
                    </h4>
                    <div className="space-y-3">
                      {atlasNavigationResult.location.landmarks.map((landmark: any, idx: number) => (
                        <div
                          key={idx}
                          className="p-4 rounded-lg"
                          style={{
                            background: "#f8f7ff",
                            border: `1px solid ${primaryColor}20`,
                          }}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <div className="font-semibold text-sm mb-1" style={{ color: cardTextColor }}>
                                {landmark.title}
                              </div>
                              {landmark.id && (
                                <div className="text-xs opacity-60 font-mono mb-1" style={{ color: cardTextColor }}>
                                  ID: {landmark.id}
                                </div>
                              )}
                              {landmark.url && (
                                <div className="text-xs opacity-60 font-mono mb-1 truncate" style={{ color: cardTextColor }}>
                                  {landmark.url}
                                </div>
                              )}
                              {landmark.topicNeighbourhood && (
                                <div className="text-xs opacity-70 mb-1" style={{ color: cardTextColor }}>
                                  Neighbourhood: {landmark.topicNeighbourhood}
                                </div>
                              )}
                              {landmark.summary && (
                                <div className="text-xs opacity-75 mt-2 mb-2" style={{ color: cardTextColor }}>
                                  {landmark.summary}
                                </div>
                              )}
                              {landmark.structuredFacts && landmark.structuredFacts.length > 0 && (
                                <div className="mt-2 space-y-1">
                                  {landmark.structuredFacts.map((fact: string, i: number) => (
                                    <div key={i} className="text-xs opacity-70 flex items-center gap-2" style={{ color: cardTextColor }}>
                                      <span className="opacity-50">•</span>
                                      <span>{fact}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {landmark.content && (
                                <div className="text-xs opacity-60 mt-2 line-clamp-2" style={{ color: cardTextColor }}>
                                  {landmark.content.substring(0, 200)}
                                  {landmark.content.length > 200 ? "..." : ""}
                                </div>
                              )}
                              {landmark.metadata && Object.keys(landmark.metadata).length > 0 && (
                                <div className="mt-2 p-2 rounded" style={{ background: "#f8f7ff" }}>
                                  <div className="text-xs opacity-60 mb-1" style={{ color: cardTextColor }}>
                                    Metadata:
                                  </div>
                                  <div className="text-xs opacity-70 font-mono" style={{ color: cardTextColor }}>
                                    {JSON.stringify(landmark.metadata, null, 2).substring(0, 150)}...
                                  </div>
                                </div>
                              )}
                            </div>
                            {landmark.reliabilityScore !== undefined && (
                              <div className="ml-3 flex flex-col items-end">
                                <div className="text-lg font-bold" style={{ color: cardTextColor }}>
                                  {(landmark.reliabilityScore * 100).toFixed(2)}%
                                </div>
                                <div className="text-xs opacity-60" style={{ color: cardTextColor }}>
                                  Reliability
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              {atlasNavigationResult?.suggestions && atlasNavigationResult.suggestions.length > 0 && (
                <div
                  className="p-5 rounded-2xl mb-4"
                  style={{
                    background: "#ffffff",
                    border: `1px solid ${primaryColor}25`,
                    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                  }}
                >
                  <h4 className="text-sm font-bold uppercase tracking-wide mb-3 opacity-90" style={{ color: cardTextColor }}>
                    Suggestions ({atlasNavigationResult.suggestions.length})
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {atlasNavigationResult.suggestions.map((suggestion, idx) => (
                      <span
                        key={idx}
                        className="text-sm px-3 py-1.5 rounded-lg"
                        style={{
                          background: "#f8f7ff",
                          border: `1px solid ${primaryColor}25`,
                          color: cardTextColor,
                        }}
                      >
                        {suggestion}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {atlasNavigationResult?.message && (
                <div
                  className="p-4 rounded-xl mb-4"
                  style={{
                    background: "#ffffff",
                    border: `1px solid ${primaryColor}25`,
                    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                  }}
                >
                  <div className="text-sm opacity-80" style={{ color: cardTextColor }}>
                    {atlasNavigationResult.message}
                  </div>
                </div>
              )}

              {atlasNavigationResult?.success !== undefined && (
                <div
                  className="p-3 rounded-lg mb-4 flex items-center gap-2"
                  style={{
                    background: atlasNavigationResult.success ? "#f0eeff" : "#f5f0ff",
                    border: `1px solid ${atlasNavigationResult.success ? primaryColor : secondaryColor}25`,
                  }}
                >
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{
                      background: atlasNavigationResult.success ? primaryColor : secondaryColor,
                    }}
                  />
                  <span className="text-xs font-medium" style={{ color: cardTextColor }}>
                    Navigation {atlasNavigationResult.success ? "Successful" : "Failed"}
                  </span>
                </div>
              )}
            </div>

            {atlasNavigationResult && (
              <div className="mt-8">
                <h3 className="text-sm font-bold uppercase tracking-wide mb-4 opacity-80" style={{ color: cardTextColor }}>
                  COGNITIVE MAP VISUALIZATION
                </h3>
                <CognitiveMapGraph
                  paths={[
                    ...(atlasNavigationResult.location?.landmarks || []).map((landmark: any, idx: number) => ({
                      id: landmark.id || `landmark-${idx}`,
                      label: landmark.title,
                      round: 1,
                      content: landmark.summary || landmark.title,
                      confidence: landmark.reliabilityScore || 0.5,
                      convergenceScore: landmark.metadata?.verificationLevel === "High" ? 0.9 : 0.6,
                    })),
                    ...(atlasNavigationResult.orientation?.nearbyTopics || []).map((topic: any, idx: number) => ({
                      id: topic.id || `topic-${idx}`,
                      label: topic.name,
                      round: 2,
                      content: topic.description || topic.name,
                      confidence: topic.familiarity || 0.5,
                    })),
                  ]}
                  primaryColor={primaryColor}
                  secondaryColor={secondaryColor}
                  onPathSelect={(pathId) => {
                    console.log("Selected cognitive path:", pathId);
                  }}
                />
              </div>
            )}

            {atlasNavigationResult?.location?.landmarks && atlasNavigationResult.location.landmarks.length > 0 && (
              <div className="mt-6">
                <h3 className="text-sm font-bold uppercase tracking-wide mb-4 opacity-80" style={{ color: cardTextColor }}>
                  CONFIDENCE & RELIABILITY
                </h3>
                <div className="space-y-4">
                  {atlasNavigationResult.location.landmarks.slice(0, 5).map((landmark: any, idx: number) => (
                    <ConfidenceIndicator
                      key={idx}
                      score={landmark.reliabilityScore || 0.5}
                      label={landmark.title || `Landmark ${idx + 1}`}
                      primaryColor={primaryColor}
                    />
                  ))}
                </div>
              </div>
            )}

            {atlasHistory && atlasHistory.length > 0 && (
              <div className="mt-8">
                <h3 className="text-sm font-bold uppercase tracking-wide mb-4 opacity-80" style={{ color: cardTextColor }}>
                  COGNITIVE EXPLORATION HISTORY
                </h3>
                <div className="space-y-3">
                  {atlasHistory.map((entry, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.1 }}
                      className="p-4 rounded-xl cursor-pointer hover:scale-[1.02] transition-transform"
                      style={{
                        background: "#f8f7ff",
                        border: `1px solid ${primaryColor}25`,
                      }}
                      onClick={() => {
                        console.log("Navigate to historical point:", entry);
                      }}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Compass className="w-4 h-4" style={{ color: cardTextColor }} />
                            <span className="text-sm font-semibold" style={{ color: cardTextColor }}>
                              {entry.result?.location?.topicName ||
                                entry.result?.orientation?.currentTopic ||
                                "Navigation"}
                            </span>
                          </div>
                          <div className="text-xs opacity-70 mb-2" style={{ color: cardTextColor }}>
                            {new Date(entry.timestamp).toLocaleString()}
                          </div>
                          {entry.result?.location?.landmarks && (
                            <div className="flex items-center gap-4 text-xs opacity-60" style={{ color: cardTextColor }}>
                              <span>{entry.result.location.landmarks.length} landmarks</span>
                              <span>{entry.result.orientation?.nearbyTopics?.length || 0} nearby areas</span>
                            </div>
                          )}
                        </div>
                        <Route className="w-5 h-5 opacity-40" style={{ color: cardTextColor }} />
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </motion.div>

        <div className="flex justify-center pb-4">
          <div
            className="w-16 h-1.5 rounded-full"
            style={{
              background: `linear-gradient(90deg, ${primaryColor}70, ${secondaryColor}80, ${primaryColor}70)`,
            }}
          />
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
